import * as d3 from "d3";
import _ from "lodash";
import $ from "jquery";
import moment from "moment";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Button, Card, Divider, Dropdown, Header, Icon, Input, Label, Segment, Select } from "semantic-ui-react";
import { restRequest } from "../../../http";
import { PlotChannelColors } from "../../../models/common_model";
import { DATA_TYPE_ARRAY, DATA_TYPE_SCALAR, DATA_TYPE_VECTOR, HistogramData } from "../../../models/testing_model";

const statusColors = ['', PlotChannelColors[0], 'darkred'];

export function HistogramPlot(props: { match: any }) {
    const id = props.match.params.id as string;
    const params = JSON.parse(decodeURIComponent(window.atob(props.match.params.params))) as any;
    const {measurement_name, export_file_name, measurement_key} = params;
    const maxChannel = params.maxChannel - 0;
    const dataType = params.dataType - 0;
    const productName = params.productName;
    const searchParams = _.omit(params, 'measurementKey', 'maxChannel', 'dataType', 'productName');

    const [selectedChannel, setSelectedChannel] = useState<number>(1);
    const [selectedStatuses, setSelectedStatuses] = useState<boolean[]>([true, true]);
    const [values, setValues] = useState<number[]>([]);
    const [data, setData] = useState<HistogramData[]>();
    const [binSize, setBinSize] = useState(0.01);

    const [currentX, setCurrentX] = useState(dataType === DATA_TYPE_VECTOR ? 1000 : 0);
    const [finalX, setFinalX] = useState(dataType === DATA_TYPE_VECTOR ? 1000 : 0);

    const svgRootRef = useRef<HTMLDivElement>(null);
    const [canvasSize, setCanvasSize] = useState([0, 0]);
    const margin = ({ top: 20, right: 30, bottom: 30, left: 50 });
    const [currentInfo, setCurrentInfo] = useState({
        x0: '',
        x1: '',
        count: 0,
        isShow: false
    })
    const [loading, setLoading] = useState(false);
    const [gpibChannelNames, setGpibChannelNames] = useState<string[]>([]);
    const [xType, setxType] = useState(2);
    const [gpibName, setGpibName] = useState('');
    const [isReady, setIsReady] = useState(false); 

    useEffect(() => {
        if (id.startsWith('lot_')) {
            setGpibChannelNames([]);
            setGpibName('');
            setxType(1);
            setIsReady(true);
        } else {
            setLoading(true);
            restRequest<string[]>(`testings/gpib_channel_names/${id}`, {measurement_key, channel: selectedChannel}, 'post').then(data => {
                setGpibChannelNames(data || []);
                if (!data || data.length === 0) {
                    setxType(1);
                } else {
                    if (!gpibName) {
                        setGpibName(data[0]);
                    }
                }
            }).finally(() => {
                setLoading(false);
                setIsReady(true);
            })
        }
    }, [id, dataType, measurement_name, export_file_name, selectedChannel])

    useEffect(() => {
        if (xType === 1) {
            setGpibName('');
        }
    }, [xType]);

    useEffect(() => {
        if (!isReady)
        {
            return;
        }

        setLoading(true);
        const params: any = { ...searchParams, measurement_key, channel: selectedChannel };
        if (dataType === DATA_TYPE_ARRAY) {
            if (gpibName) {
                params['gpib_name'] = gpibName
            } else {
                params['index'] = finalX
            }
        } else if (dataType === DATA_TYPE_VECTOR) {
            params['x'] = finalX
        }
        restRequest<HistogramData[]>(`testings/overview_parameters_for_histogram/${id}`, params, 'post').then(data => {
            setData(data);
        }).finally(() => setLoading(false));
    }, [id, measurement_name, export_file_name, selectedChannel, finalX, gpibName, isReady]);

    useLayoutEffect(() => {
        document.title = productName + ' ------ ' + measurement_name + ' Histogram'

        const onResize = function () {
            if (svgRootRef.current) {
                const offset = $(svgRootRef.current).offset()!;
                const width = $(svgRootRef.current).innerWidth()!;

                setCanvasSize([width - 20, window.innerHeight - offset.top - 40]);
            }
        };
        $(window).on('resize', onResize);
        onResize();

        return () => {
            $(window).off('resize', onResize);
        }
    }, []);

    useEffect(() => {
        if (!data) {
            return;
        }

        let allValues: number[];
        if (selectedStatuses.filter(s => s).length === selectedStatuses.length) {
            allValues = data.map(d => d.value);
        } else if (selectedStatuses[0]) {
            allValues = data.filter(d => d.status === 1).map(d => d.value);
        } else if (selectedStatuses[1]) {
            allValues = data.filter(d => d.status === 2).map(d => d.value);
        } else {
            setBinSize(0.01);
            setValues([]);
            return;
        }
        if (allValues.length === 0) {
            setBinSize(0.01);
            setValues([]);
            return;
        }
   
        const sortedAllValues = d3.sort(allValues)
        const iqr = Math.abs(d3.quantileSorted(sortedAllValues, 0.75)! - d3.quantileSorted(sortedAllValues, 0.25)!);
        const tempBinSize = 2 * iqr / Math.pow(allValues.length, 1/3.0);
        setBinSize(parseFloat((tempBinSize).toFixed(Math.max(3, Math.ceil(Math.log10(1/tempBinSize) + 1)))));

        setValues(allValues);
    }, [data, selectedStatuses])

    useEffect(() => {
        if (values.length === 0) {
            return;
        }

        const xDomain = d3.extent(values) as number[];

        const [width, height] = canvasSize;

        const svg = d3.select(svgRootRef.current!)
            .append("svg")
            .attr("viewBox", [0, 0, width, height] as any);


        const xTicks = Math.ceil((xDomain[1] - xDomain[0]) / binSize);

        const x = d3.scaleLinear().domain(xDomain).range([margin.left, width - margin.right]);

        var histogram = d3.bin().domain(xDomain as any).thresholds(x.ticks(xTicks));

        // And apply this function to data to get the bins
        var bins = histogram(values);

        const yDomain = [0, d3.max(bins, d => d.length)] as number[];

        const y = d3.scaleLinear().domain(yDomain).range([height - margin.bottom, margin.top]);

        const xAxis = (g: any) => {
            g.attr("transform", `translate(0,${height - margin.bottom})`)
                .call(d3.axisBottom(x as any).ticks(xTicks).tickSizeInner(-height + margin.top + margin.bottom));
        }
        svg.append("g").attr("class", "x-axis-inner").call(xAxis);

        const yAxis = (g: any) => {
            g.attr("transform", `translate(${margin.left},0)`).call(d3.axisLeft(y).ticks(20).tickSizeInner(-width + margin.left + margin.right));
        }
        svg.append("g").attr("class", "y-axis-inner").call(yAxis);

        svg
            .selectAll("rect")
            .data(bins)
            .enter()
            .append("rect")
            .attr("x", 1)
            .attr("transform", function (d) {
                return "translate(" + x(d.x0 as any) + "," + y(d.length) + ")";
            })
            .attr("width", function (d) {
                return x(d.x1 as any) - x(d.x0 as any) - 1;
            })
            .attr("height", function (d) {
                return height - margin.bottom - y(d.length);
            })
            .style("fill", "#69b3a2");


        //focus
        const focus = svg.append('g').style('display', 'none');

        focus.append('line')
            .attr('id', 'focusLineX')
            .attr('class', 'focusLine')
            .attr('y1', y(yDomain[0]))
            .attr('y2', y(yDomain[1]));

        focus.append('circle')
            .attr('id', 'focusCircle')
            .attr('r', 5)
            .attr('class', 'circle focusCircle');
        focus.append('line')
            .attr('id', 'focusLineY')
            .attr('class', 'focusLine')
            .attr('x1', x(xDomain[0]))
            .attr('x2', x(xDomain[1]));

        svg.append('rect')
            .attr('class', 'overlay')
            .attr('width', width)
            .attr('height', height)
            .on('mouseover', function () { focus.style('display', null); })
            .on('mouseout', function () {
                focus.style('display', 'none');
                setCurrentInfo(info => ({ ...info, isShow: false }))
            })
            .on('mousemove', function (event) {
                const mouse = d3.pointer(event);
                const [mx, my] = mouse
                const mouseX = x.invert(mx);

                let find = false;
                for (const b of bins) {
                    if (b.length > 0 && b.x0! < mouseX && b.x1! > mouseX) {
                        setCurrentInfo({ isShow: true, x0: b.x0!.toExponential(2), x1: b.x1!.toExponential(2), count: b.length });
                        find = true;
                        break;
                    }
                }

                if (!find) {
                    setCurrentInfo(v => ({ ...v, isShow: false }));
                }

                focus.select('#focusLineX')
                    .attr('x1', mx)
                    .attr('x2', mx).attr('y2', y(yDomain[1]));
                focus.select('#focusCircle')
                    .attr('cx', mx)
                    .attr('cy', mouse[1]);
                focus.select('#focusLineY')
                    .attr('y1', my)
                    .attr('y2', my);

            });

        return () => {
            svg.remove()
        }
    }, [canvasSize, values, binSize]);


    return <div style={{ padding: '0 1rem' }}>
        <Header attached="top">{productName} &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; {measurement_name} <sub>{export_file_name}</sub></Header>
        <Segment attached="bottom" compact basic style={{ padding: '0', margin: '0' }} loading={loading}>
            <Segment.Group horizontal={true} compact style={{ padding: '2px', margin: '0', border: 'none' }}>
                <Segment style={{ width: '250px', flex: 'inherit' }}>
                    {maxChannel > 1 && <>
                        <Card>
                            <Card.Header textAlign="center">Channels</Card.Header>
                            <Card.Content>
                                <Button.Group compact>
                                    {_.range(1, maxChannel + 1).map(c => <Button key={c} secondary basic={selectedChannel !== c} onClick={e => {
                                        setSelectedChannel(c);
                                    }}>CH{c}</Button>)}
                                </Button.Group>
                            </Card.Content>
                        </Card>
                        <Divider></Divider>
                    </>}

                    {dataType !== DATA_TYPE_SCALAR && <>
                        {gpibChannelNames && gpibChannelNames.length > 0 && <Select value={xType} onChange={(e, d) => setxType(d.value as number)} options={[{key: 1, value: 1, text: 'By Index'}, {key: 2, value: 2, text: 'By GPIB Channel Name'}]} fluid></Select>}

                        {!(xType === 2 && gpibChannelNames.length > 0) && <>
                            <Input action={dataType === DATA_TYPE_ARRAY ? 'index' : 'x'} actionPosition="left" fluid input={<input value={currentX} onChange={(e) => {
                                try {
                                    const val = dataType === DATA_TYPE_ARRAY ? parseInt(e.target.value as string) : parseFloat(e.target.value as string);
                                    if (!isNaN(val)) {
                                        setCurrentX(val);
                                    }
                                } catch (e) { }
                            }} onBlur={() => {
                                setFinalX(currentX);
                            }} min={0} />}>
                            </Input>
                            <Divider></Divider>
                        </>}
                        {xType === 2 && gpibChannelNames.length > 0 && <Dropdown value={gpibName} onChange={(e, d) => setGpibName(d.value as string)} selection search options={gpibChannelNames.map(v => {
                            const vs = v.split('#', 2)
                            return {key: v, value: v, text: vs[1].toLocaleLowerCase() != 'none' ? vs[1] : `Port ${vs[0]}`};
                        })} fluid></Dropdown>}
                    </>}

 

                    <Header attached="top" size="tiny">Selected</Header>
                    <Segment attached="bottom" size="tiny">
                        <div>
                            <div>
                                X: {currentInfo.isShow ? (currentInfo.x0 + ' ~ ' + currentInfo.x1) : ''}
                            </div>
                            <div>
                                Y: {currentInfo.isShow ? currentInfo.count : ''}
                            </div>
                        </div>
                    </Segment>

                    <Divider></Divider>

                    <Card>
                        <Card.Header textAlign="center">Status</Card.Header>
                        <Card.Content>
                            <Button.Group compact>
                                {['Pass', 'Fail'].map((status, i) => <Button key={i} secondary basic={!selectedStatuses[i]} onClick={e => {
                                    setSelectedStatuses(ss => ss.map((s, si) => si === i ? !s : s));
                                }}>{status}</Button>)}
                            </Button.Group>
                        </Card.Content>
                    </Card>

                    <Card>
                        <Card.Header textAlign="center">BIN Size</Card.Header>
                        <Card.Content>
                            <Input value={binSize} step="0.001" type="number" onChange={(e, d) => {
                                try {
                                    setBinSize(parseFloat(d.value));
                                } catch (e) { }
                            }} />
                        </Card.Content>
                    </Card>
                </Segment>
                <Segment>
                    <div style={{ float: 'right', position: 'relative', width: `100%` }} ref={svgRootRef}></div>
                </Segment>
            </Segment.Group>
        </Segment>
    </div>
}
